import 'intersection-observer'

import React, { Component } from 'react'
import { InView } from 'react-intersection-observer'

import { withAnalyticsClient } from '@acorns/web-utils'
import { withSafeTimeout } from '@hocs/safe-timers'
import { compose, defaultProps } from 'recompose'

import withAnalyticsIdentity from 'utils/with-analytics-identity'

import defaults from './defaults'
import verifyRender from './verifyRender'
import withFields from './withFields'

type Props = {
  fields?: object
  multiple?: boolean
  onViewed?: (payload: object) => void
  skipAnalytics?: boolean
  threshold?: number
  viewType?: ViewType
}

type AddedProps = {
  setSafeTimeout: (fn: () => void, time: number) => () => void
} & any

type EnhancedProps = Props & AddedProps

export enum ViewType {
  Container = 'Container Viewed',
  Screen = 'Screen Viewed',
}

const enhance = compose<EnhancedProps, Props>(
  defaultProps({
    fields: {},
    threshold: 1,
    onViewed: () => {
      /* NO-OP */
    },
  }),
  withAnalyticsClient,
  withSafeTimeout,
  withAnalyticsIdentity,
)

export class TrackViewedComponent extends Component<EnhancedProps, any> {
  public timeoutHandle: (() => void) | null = null

  public tracked = false

  public visible = false

  public defaultFields = defaults(
    this.props.viewType ? this.props.viewType : 'Container Viewed',
  )

  public onViewed() {
    const payload = {
      ...this.defaultFields,
      ...this.props.fields,
    }

    if (!this.props.skipAnalytics) {
      const viewType = this.props.viewType || 'Container Viewed'
      this.props.analytics.track(viewType, payload)
    }

    // Note: Provide a hook for this logic for external usage
    this.props.onViewed(payload)

    this.tracked = true
  }

  public handleChanged = (visible: boolean) => {
    if ((!this.props.multiple && this.tracked) || !verifyRender(this)) {
      return
    }

    if (visible) {
      this.timeoutHandle = this.props.setSafeTimeout(() => {
        this.onViewed()
      }, 1000)
    } else if (this.timeoutHandle) {
      this.timeoutHandle()

      this.timeoutHandle = null
    }

    this.visible = visible
  }

  public render() {
    return (
      <InView onChange={this.handleChanged} threshold={this.props.threshold}>
        {this.props.children}
      </InView>
    )
  }
}

export const TrackViewed = enhance(TrackViewedComponent)

export const withTrackViewed = <InnerProps, OuterProps>(
  customFields: object | ((props: any) => object),
  viewType = ViewType.Container,
) =>
  compose<InnerProps, OuterProps>(
    withFields(customFields),
    (WrappedComponent: any) =>
      ({ fields, threshold, ...props }: EnhancedProps) => (
        <TrackViewed
          fields={fields}
          onViewed={props.onViewed}
          threshold={threshold}
          viewType={viewType}
        >
          <WrappedComponent {...props} />
        </TrackViewed>
      ),
  )
